// Utilitários compartilhados para o campo de telefone (seletor de país + número).
// Padrão: EUA (+1). Máscara: (999) 999-9999 (+1) e (99) 99999-9999 (+55).

export interface CountryOption {
  code: string;
  flag: string;
  name: string;
  placeholder: string;
  digits: number;
}

export const COUNTRY_OPTIONS: CountryOption[] = [
  { code: "+1", flag: "🇺🇸", name: "EUA", placeholder: "(999) 999-9999", digits: 10 },
  { code: "+55", flag: "🇧🇷", name: "Brasil", placeholder: "(99) 99999-9999", digits: 11 },
];

export const DEFAULT_COUNTRY_CODE = "+1";

// Aplica a máscara conforme o país. Recebe qualquer texto e devolve formatado.
export function formatPhone(raw: string, code: string): string {
  const digits = raw.replace(/\D/g, "");
  if (code === "+55") {
    if (digits.length <= 2) return digits;
    if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
    if (digits.length <= 11) return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7, 11)}`;
  }
  if (code === "+1") {
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    if (digits.length <= 10) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  }
  return digits;
}

// Remove o código do país (se presente) e devolve só os dígitos.
export function stripCountryPrefix(digits: string, code: string): string {
  const cc = code.replace(/\D/g, "");
  if (cc && digits.startsWith(cc)) {
    return digits.slice(cc.length);
  }
  return digits;
}

// Interpreta a entrada do usuário (incluindo "colar" um número já com +1/+55):
// - detecta o país automaticamente quando há sinal de "+" ou quantidade de dígitos clara;
// - nunca duplica o código do país;
// - em caso de dúvida, mantém o país atual.
export function parsePhoneInput(
  raw: string,
  currentCode: string,
): { countryCode: string; digits: string } {
  const s = raw.trim();
  const hasPlus = s.includes("+");

  if (hasPlus) {
    if (s.startsWith("+55")) {
      return { countryCode: "+55", digits: stripCountryPrefix(s.replace(/\D/g, ""), "+55") };
    }
    if (s.startsWith("+1")) {
      return { countryCode: "+1", digits: stripCountryPrefix(s.replace(/\D/g, ""), "+1") };
    }
  }

  const digits = s.replace(/\D/g, "");

  // Sem "+", mas com quantidade de dígitos que revela o país (colar "55..." ou "1...").
  if (digits.startsWith("55") && digits.length >= 12) {
    return { countryCode: "+55", digits: digits.slice(2) };
  }
  if (digits.startsWith("1") && digits.length === 11) {
    return { countryCode: "+1", digits: digits.slice(1) };
  }

  // Mantém o país atual e remove prefixo duplicado do próprio país, se houver.
  if (currentCode === "+55" && digits.startsWith("55") && digits.length > 11) {
    return { countryCode: "+55", digits: digits.slice(2) };
  }
  if (currentCode === "+1" && digits.startsWith("1") && digits.length > 10) {
    return { countryCode: "+1", digits: digits.slice(1) };
  }

  return { countryCode: currentCode, digits };
}

// Número canônico E.164: country_code + dígitos, sem espaço/pontuação. Ex.: +15619399706.
export function buildE164(countryCode: string | null | undefined, whatsapp: string | null | undefined): string | null {
  const digits = (whatsapp || "").replace(/\D/g, "");
  if (!digits) return null;
  return `${countryCode || ""}${digits}`;
}

// Indica se o número está fora do padrão esperado para o país (para sinalizar no admin).
export function isInvalidWhatsapp(
  countryCode: string | null | undefined,
  whatsapp: string | null | undefined,
): boolean {
  const digits = (whatsapp || "").replace(/\D/g, "");
  if (!digits) return false; // sem número não é "inválido", só ausente
  if (countryCode === "+55") return digits.length !== 11;
  if (countryCode === "+1") return digits.length !== 10;
  return false; // país desconhecido: não dá para julgar
}